import React, { useState } from 'react'

import { Popover, useTheme } from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'

import { Container } from '../../components'

interface Props {
    startDate: Date
    endDate: Date
    onDateChange: (startDate: Date, endDate: Date) => void
}

export const DateRangeChip: React.FC<Props> = ({
    startDate,
    endDate,
    onDateChange,
}) => {
    const theme = useTheme()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const [tempStartDate, setTempStartDate] = useState<Date | null>(startDate)
    const [tempEndDate, setTempEndDate] = useState<Date | null>(endDate)

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
        setTempStartDate(startDate)
        setTempEndDate(endDate)
    }

    const handleClose = () => {
        if (tempStartDate && tempEndDate) {
            // Only update if dates actually changed
            if (
                tempStartDate.getTime() !== startDate.getTime() ||
                tempEndDate.getTime() !== endDate.getTime()
            ) {
                onDateChange(tempStartDate, tempEndDate)
            }
        }
        setAnchorEl(null)
    }

    const handleStartDateChange = (date: Date | null) => {
        if (date) {
            setTempStartDate(date)
            // If new start date is after end date, update end date
            if (tempEndDate && date > tempEndDate) {
                setTempEndDate(date)
            }
        }
    }

    const handleEndDateChange = (date: Date | null) => {
        if (date) {
            setTempEndDate(date)
            // If new end date is before start date, update start date
            if (tempStartDate && date < tempStartDate) {
                setTempStartDate(date)
            }
        }
    }

    const formatDate = (date: Date) => {
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        })
    }

    return (
        <>
            <span
                onClick={handleClick}
                style={{
                    color: theme.palette.primary.main,
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                        theme.palette.primary.light
                    e.currentTarget.style.textDecoration = 'none'
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent'
                    e.currentTarget.style.textDecoration = 'underline'
                }}
            >
                {formatDate(startDate)} - {formatDate(endDate)}
            </span>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <Container
                    style={{
                        padding: theme.spacing(2),
                        flexDirection: 'column',
                    }}
                >
                    <KeyboardDatePicker
                        label="Created After"
                        value={tempStartDate}
                        onChange={handleStartDateChange}
                        format="MM/dd/yyyy"
                        maxDate={tempEndDate || undefined}
                        style={{ marginBottom: theme.spacing(2) }}
                    />
                    <KeyboardDatePicker
                        label="Created Before"
                        value={tempEndDate}
                        onChange={handleEndDateChange}
                        format="MM/dd/yyyy"
                        minDate={tempStartDate || undefined}
                    />
                </Container>
            </Popover>
        </>
    )
}
